import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Link,
} from '@mui/material';
import { Email, ArrowBack, Visibility, VisibilityOff, LockReset, CheckCircle, RadioButtonUnchecked } from '@mui/icons-material';
import axiosInstance from '../../../api/axiosConfig';
import { extractErrorMessage } from '../../../utils/errorUtils';

const STEPS = { EMAIL: 0, CODE: 1, SUCCESS: 2 };

const PASSWORD_RULES = [
  { key: 'length', label: 'Mínimo 8 caracteres', test: (p) => p.length >= 8 },
  { key: 'uppercase', label: 'Al menos una letra mayúscula', test: (p) => /[A-Z]/.test(p) },
  { key: 'lowercase', label: 'Al menos una letra minúscula', test: (p) => /[a-z]/.test(p) },
  { key: 'number', label: 'Al menos un número', test: (p) => /[0-9]/.test(p) },
  { key: 'special', label: 'Al menos un carácter especial (!@#$%^&*...)', test: (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p) },
];

const PasswordChecklist = ({ password }) => (
  <Box sx={{ mt: 1.5, mb: 2, p: 2, borderRadius: 0, bgcolor: '#f2f2f3', border: '1px solid rgba(29,31,32,0.16)' }}>
    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'rgba(29,31,32,0.55)', mb: 1, letterSpacing: '0.03em' }}>
      Requisitos de la contraseña
    </Typography>
    {PASSWORD_RULES.map((rule) => {
      const passed = password.length > 0 && rule.test(password);
      return (
        <Box key={rule.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.35 }}>
          {passed ? (
            <CheckCircle sx={{ fontSize: 16, color: '#16A34A' }} />
          ) : (
            <RadioButtonUnchecked sx={{ fontSize: 16, color: '#CBD5E1' }} />
          )}
          <Typography
            sx={{
              fontSize: '0.8rem',
              color: passed ? '#16A34A' : '#98989b',
              fontWeight: passed ? 500 : 400,
              transition: 'color 0.2s',
            }}
          >
            {rule.label}
          </Typography>
        </Box>
      );
    })}
  </Box>
);

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(STEPS.EMAIL);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const codeInputsRef = useRef([]);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Ingresa tu correo electronico');
      return;
    }
    try {
      setError('');
      setLoading(true);
      await axiosInstance.post('/auth/request-password-reset', { email: email.trim() });
      setSuccess('Si el correo esta registrado, recibiras un codigo de 6 digitos.');
      setStep(STEPS.CODE);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const digits = code.split('');
    while (digits.length < 6) digits.push('');
    digits[index] = value;
    const newCode = digits.join('');
    setCode(newCode);
    if (value && index < 5) {
      codeInputsRef.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeInputsRef.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    setCode(pasted);
    const nextIndex = Math.min(pasted.length, 5);
    codeInputsRef.current[nextIndex]?.focus();
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (code.length !== 6) {
      setError('Ingresa el código de 6 dígitos');
      return;
    }
    const failedRules = PASSWORD_RULES.filter((r) => !r.test(password));
    if (failedRules.length > 0) {
      setError('La contraseña no cumple: ' + failedRules.map((r) => r.label.toLowerCase()).join(', '));
      return;
    }
    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    try {
      setLoading(true);
      await axiosInstance.post('/auth/reset-password', {
        email: email.trim(),
        code,
        password,
      });
      setStep(STEPS.SUCCESS);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setError('');
      setLoading(true);
      await axiosInstance.post('/auth/request-password-reset', { email: email.trim() });
      setSuccess('Nuevo codigo enviado a tu correo.');
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: '#f2f2f3',
        px: 3,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 420 }}>
        {/* Back link */}
        <Link
          component="button"
          onClick={() => (step === STEPS.CODE ? setStep(STEPS.EMAIL) : navigate('/auth/login'))}
          underline="hover"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            color: 'rgba(29,31,32,0.55)',
            fontSize: '0.875rem',
            mb: 4,
            cursor: 'pointer',
          }}
        >
          <ArrowBack sx={{ fontSize: 18 }} />
          {step === STEPS.CODE ? 'Cambiar correo' : 'Volver al inicio de sesion'}
        </Link>

        {/* Step: Email */}
        {step === STEPS.EMAIL && (
          <>
            <Box sx={{ mb: 4 }}>
              <LockReset sx={{ fontSize: 48, color: '#5980a6', mb: 2 }} />
              <Typography
                component="h1"
                sx={{ fontWeight: 800, fontSize: '1.75rem', color: '#1d2d3d', letterSpacing: '-0.03em', mb: 1 }}
              >
                Recuperar contrasena
              </Typography>
              <Typography sx={{ color: 'rgba(29,31,32,0.55)', fontSize: '0.9375rem' }}>
                Ingresa tu correo y te enviaremos un codigo de verificacion.
              </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <form onSubmit={handleRequestCode}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight={500} gutterBottom sx={{ mb: 1 }}>
                  Correo corporativo
                </Typography>
                <TextField
                  fullWidth
                  type="email"
                  placeholder="nombre@empresa.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Email sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
                />
              </Box>

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={loading}
                sx={{
                  py: 1.625,
                  fontSize: '0.9375rem',
                  fontWeight: 700,
                  borderRadius: 0,
                  bgcolor: '#5980a6',
                  '&:hover': { bgcolor: '#416180' },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Enviar codigo'}
              </Button>
            </form>
          </>
        )}

        {/* Step: Code + New Password */}
        {step === STEPS.CODE && (
          <>
            <Box sx={{ mb: 4 }}>
              <LockReset sx={{ fontSize: 48, color: '#5980a6', mb: 2 }} />
              <Typography
                component="h1"
                sx={{ fontWeight: 800, fontSize: '1.75rem', color: '#1d2d3d', letterSpacing: '-0.03em', mb: 1 }}
              >
                Ingresa el codigo
              </Typography>
              <Typography sx={{ color: 'rgba(29,31,32,0.55)', fontSize: '0.9375rem' }}>
                Enviamos un codigo de 6 digitos a <strong>{email}</strong>
              </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            <form onSubmit={handleResetPassword}>
              {/* Code inputs */}
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 3 }}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <TextField
                    key={i}
                    inputRef={(el) => (codeInputsRef.current[i] = el)}
                    value={code[i] || ''}
                    onChange={(e) => handleCodeChange(i, e.target.value)}
                    onKeyDown={(e) => handleCodeKeyDown(i, e)}
                    onPaste={i === 0 ? handleCodePaste : undefined}
                    inputProps={{
                      maxLength: 1,
                      style: { textAlign: 'center', fontSize: '1.5rem', fontWeight: 700, padding: '12px 0' },
                      inputMode: 'numeric',
                    }}
                    sx={{ width: 52, '& .MuiOutlinedInput-root': { bgcolor: '#e9e9ea' } }}
                  />
                ))}
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight={500} gutterBottom sx={{ mb: 1 }}>
                  Nueva contrasena
                </Typography>
                <TextField
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Minimo 8 caracteres"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
                />
                <PasswordChecklist password={password} />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight={500} gutterBottom sx={{ mb: 1 }}>
                  Confirmar contraseña
                </Typography>
                <TextField
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Repite la contrasena"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}
                />
              </Box>

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={loading}
                sx={{
                  py: 1.625,
                  fontSize: '0.9375rem',
                  fontWeight: 700,
                  borderRadius: 0,
                  bgcolor: '#5980a6',
                  '&:hover': { bgcolor: '#416180' },
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Restablecer contrasena'}
              </Button>

              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Link
                  component="button"
                  type="button"
                  onClick={handleResendCode}
                  underline="hover"
                  sx={{ color: '#5980a6', fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer' }}
                >
                  Reenviar codigo
                </Link>
              </Box>
            </form>
          </>
        )}

        {/* Step: Success */}
        {step === STEPS.SUCCESS && (
          <Box sx={{ textAlign: 'center' }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: '#DCFCE7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
              }}
            >
              <LockReset sx={{ fontSize: 32, color: '#16A34A' }} />
            </Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.75rem', color: '#1d2d3d', mb: 1 }}>
              Contrasena actualizada
            </Typography>
            <Typography sx={{ color: 'rgba(29,31,32,0.55)', fontSize: '0.9375rem', mb: 4 }}>
              Tu contrasena ha sido restablecida exitosamente. Ya puedes iniciar sesion.
            </Typography>
            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={() => navigate('/auth/login')}
              sx={{
                py: 1.625,
                fontSize: '0.9375rem',
                fontWeight: 700,
                borderRadius: 0,
                bgcolor: '#5980a6',
                '&:hover': { bgcolor: '#416180' },
              }}
            >
              Ir a iniciar sesion
            </Button>
          </Box>
        )}

        <Typography sx={{ textAlign: 'center', mt: 5, color: '#CBD5E1', fontSize: '0.75rem' }}>
          &copy; 2026 Simplia - Gestion Documental Inteligente
        </Typography>
      </Box>
    </Box>
  );
};

export default ForgotPasswordPage;
